import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import { InputSwitch } from 'primereact/inputswitch'
import type { LocalizedText } from '../../../lib/localizedText'
import Button from '../../../components/ui/Button'
import Badge from '../../../components/ui/Badge'
import SortSpinner from '../../../components/ui/SortSpinner'
import type { ClassificationLookup } from '../types'

interface ColumnOpts {
  t: TFunction
  /** Ekran metinlerinin i18n öneki (`config.ts` → `i18nPrefix`). */
  i18nPrefix: string
  /** Ad kolonu tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  busyId: number | null
  /** Görünen satır sayısı — son satırda "aşağı" pasif olması için. */
  rowCount: number
  /** Yeniden sıralama sürüyor mu — oklar kilitlenir. */
  reordering: boolean
  onMove: (row: ClassificationLookup, dir: 'up' | 'down') => void
  onEdit: (row: ClassificationLookup) => void
  onToggle: (row: ClassificationLookup, active: boolean) => void
  onDelete: (row: ClassificationLookup) => void
  /**
   * 126 — Kısıtlı Yetkili asistan: sıra ve işlem kolonları hiç çizilmez (salt okunur); durum, rozet
   * kolonundan okunur.
   */
  readOnly?: boolean
}

/**
 * Stratejik hedef / katkı tablosunun sütunları (127; paylaşılan ui/DataTable'a beslenir). Baştaki **sıra**
 * kolonu ↑/↓ okları (ortak SortSpinner) — sıra numarası gösterilmez, taşıma anında kalıcı. Durum
 * satır-içi InputSwitch (Aktif/Pasif) + satır aksiyonları (düzenle/sil). Korumalı sistem kaydı yoktur.
 * (`EventTypeColumns` deseniyle aynı.)
 */
export function classificationLookupColumns({
  t,
  i18nPrefix,
  localized,
  busyId,
  rowCount,
  reordering,
  onMove,
  onEdit,
  onToggle,
  onDelete,
  readOnly = false,
}: ColumnOpts): ReactElement[] {
  const k = (suffix: string) => t(`${i18nPrefix}.${suffix}`)
  const nameColumn = (
    <Column
      key="name"
      header={k('columns.name')}
      body={(row: ClassificationLookup) => localized(row.nameTr, row.nameEn)}
    />
  )
  const statusColumn = (
    <Column
      key="status"
      header={k('columns.status')}
      style={{ width: '8rem' }}
      body={(row: ClassificationLookup) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active ? k('status.active') : k('status.passive')}
          </Badge>
        )
      }}
    />
  )
  if (readOnly) return [nameColumn, statusColumn]
  return [
    // Sıra kolonu — yalnızca oklar (SORT_INDEX değeri gösterilmez); liste daima sıra düzeninde.
    <Column
      key="sort"
      header={k('columns.sortIndex')}
      style={{ width: '6rem' }}
      body={(row: ClassificationLookup, { rowIndex }) => (
        <SortSpinner
          onUp={() => onMove(row, 'up')}
          onDown={() => onMove(row, 'down')}
          disableUp={rowIndex === 0}
          disableDown={rowIndex === rowCount - 1}
          busy={reordering}
          labelUp={t('common.reorder.up')}
          labelDown={t('common.reorder.down')}
        />
      )}
    />,
    nameColumn,
    statusColumn,
    <Column
      key="actions"
      header={k('columns.actions')}
      style={{ width: '10rem' }}
      body={(row: ClassificationLookup) => {
        const active = row.recordStatus === 1
        return (
          <div className="admin-row-actions">
            <InputSwitch
              className="admin-switch"
              checked={active}
              disabled={busyId === row.id}
              onChange={(e) => onToggle(row, Boolean(e.value))}
              aria-label={row.nameTr}
              tooltip={k('actions.toggle')}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              icon="pi pi-pencil"
              rounded
              text
              aria-label={k('actions.edit')}
              tooltip={k('actions.edit')}
              tooltipOptions={{ position: 'bottom' }}
              onClick={() => onEdit(row)}
            />
            <Button
              icon="pi pi-trash"
              rounded
              text
              severity="danger"
              aria-label={k('actions.delete')}
              tooltip={k('actions.delete')}
              tooltipOptions={{ position: 'bottom' }}
              disabled={busyId === row.id}
              onClick={() => onDelete(row)}
            />
          </div>
        )
      }}
    />,
  ]
}
